import React, { useState } from "react";
import { motion } from "@/lib/framer-motion-shim";
import { Send, User, Mail, Phone, MessageSquare, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const themeStyles = {
  photographer: {
    bg: "bg-zinc-900",
    border: "border-zinc-800",
    input: "bg-transparent border-b-2 border-zinc-700 text-white placeholder-zinc-500 focus:border-white",
    button: "bg-white text-black hover:bg-zinc-200",
    text: "text-white",
    textMuted: "text-zinc-400",
    icon: "text-zinc-500",
  },
  salon: {
    bg: "bg-white",
    border: "border-rose-100",
    input: "bg-rose-50/50 border border-rose-200 text-stone-800 placeholder-stone-400 focus:border-rose-400 rounded-xl",
    button: "bg-rose-400 text-white hover:bg-rose-500",
    text: "text-stone-800",
    textMuted: "text-stone-500",
    icon: "text-rose-300",
  },
  restaurant: {
    bg: "bg-white",
    border: "border-stone-200",
    input: "bg-stone-50 border border-stone-200 text-stone-900 placeholder-stone-400 focus:border-orange-500 rounded-xl",
    button: "bg-orange-600 text-white hover:bg-orange-700",
    text: "text-stone-900",
    textMuted: "text-stone-500",
    icon: "text-orange-300",
  },
  mechanic: {
    bg: "bg-slate-800",
    border: "border-slate-700",
    input: "bg-slate-700/50 border border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 rounded-xl",
    button: "bg-blue-600 text-white hover:bg-blue-700",
    text: "text-white",
    textMuted: "text-slate-400",
    icon: "text-blue-400",
  },
};

export default function ContactForm({ demoType, variant = "default" }) {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const styles = themeStyles[demoType] || themeStyles.photographer;
  const isMinimal = variant === "minimal";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Por favor preencha todos os campos obrigatórios");
      return;
    }

    setIsSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 700));
      setIsSuccess(true);
      toast.success("Mensagem enviada com sucesso!");
      setTimeout(() => {
        setFormData({ name: "", email: "", phone: "", message: "" });
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Erro ao enviar mensagem. Tente novamente.");
    }
    setIsSubmitting(false);
  };

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`p-8 rounded-2xl ${styles.bg} text-center`}
      >
        <div className={`w-16 h-16 rounded-full ${styles.button} flex items-center justify-center mx-auto mb-4`}>
          <Check className="w-8 h-8" />
        </div>
        <h3 className={`text-xl font-semibold ${styles.text} mb-2`}>Mensagem Enviada!</h3>
        <p className={styles.textMuted}>Entraremos em contacto brevemente.</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className={`text-sm ${styles.textMuted} mb-2 block`}>Nome *</label>
          <div className="relative">
            {!isMinimal && <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${styles.icon}`} />}
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="O seu nome"
              className={`w-full ${isMinimal ? 'pl-0' : 'pl-10'} pr-4 py-3 ${styles.input} focus:outline-none transition-colors duration-200`}
              data-testid="contact-name"
            />
          </div>
        </div>
        <div>
          <label className={`text-sm ${styles.textMuted} mb-2 block`}>Email *</label>
          <div className="relative">
            {!isMinimal && <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${styles.icon}`} />}
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@exemplo.com"
              className={`w-full ${isMinimal ? 'pl-0' : 'pl-10'} pr-4 py-3 ${styles.input} focus:outline-none transition-colors duration-200`}
              data-testid="contact-email"
            />
          </div>
        </div>
      </div>

      <div>
        <label className={`text-sm ${styles.textMuted} mb-2 block`}>Telefone</label>
        <div className="relative">
          {!isMinimal && <Phone className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${styles.icon}`} />}
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+351 912 345 678"
            className={`w-full ${isMinimal ? 'pl-0' : 'pl-10'} pr-4 py-3 ${styles.input} focus:outline-none transition-colors duration-200`}
            data-testid="contact-phone"
          />
        </div>
      </div>

      <div>
        <label className={`text-sm ${styles.textMuted} mb-2 block`}>Mensagem *</label>
        <div className="relative">
          {!isMinimal && <MessageSquare className={`absolute left-3 top-3 w-5 h-5 ${styles.icon}`} />}
          <textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="A sua mensagem..."
            rows={4}
            className={`w-full ${isMinimal ? 'pl-0' : 'pl-10'} pr-4 py-3 ${styles.input} focus:outline-none transition-colors duration-200 resize-none`}
            data-testid="contact-message"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className={`w-full md:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-full ${styles.button} font-semibold disabled:opacity-50 transition-all duration-200 hover:scale-[1.02]`}
        data-testid="contact-submit"
      >
        {isSubmitting ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <Send className="w-5 h-5" />
            Enviar Mensagem
          </>
        )}
      </button>
    </form>
  );
}
